// src/components/Header.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const Header = () => {
  const { itemCount } = useCart();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  const navLinks = [
    { path: '/', label: 'Home', icon: '🏠' },
    { path: '/products', label: 'Products', icon: '🌿' },
    { path: '/categories', label: 'Categories', icon: '📦' },
    { path: '/about', label: 'About', icon: 'ℹ️' },
  ];

  const contactInfo = {
    phone: '(202) 276-4975',
    text: '(202) 276-4975',
    address: 'Washington, DC',
    hours: 'Mon-Sun: 9AM - 10PM',
    email: 'info@potcastle.com'
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('header')) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  useEffect(() => {
    if (showContactModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showContactModal]);

  const handleNavClick = (path) => {
    setIsMenuOpen(false);
    setShowContactModal(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCallNow = () => {
    window.location.href = `tel:${contactInfo.phone.replace(/\D/g, '')}`;
  };

  const handleTextNow = () => {
    window.location.href = `sms:${contactInfo.text.replace(/\D/g, '')}`;
  };

  return (
    <>
      <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="header-container">
          {/* Logo */}
          <Link to="/" onClick={() => handleNavClick('/')} className="logo">
            <div className="logo-icon">
              <span className="castle-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 8L12 3L19 8L12 13L5 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19 8V18H5V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 18V13H15V18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 13V18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="8" r="1" fill="currentColor"/>
                <circle cx="8" cy="10" r="0.5" fill="currentColor"/>
                <circle cx="16" cy="10" r="0.5" fill="currentColor"/>
                🌿
              </span>
            </div>
            <div className="logo-text">
              <span className="logo-names">POT CASTLE</span>
              <span className="logo-tagline">Premium Cannabis Delivery</span>
              <span className="logo-delivery">🚚 Delivery in DMV</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => handleNavClick(link.path)}
                className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
              >
                <span className="nav-icon">{link.icon}</span>
                <span>{link.label}</span>
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="actions">
            <button onClick={() => setShowContactModal(true)} className="action-btn contact-btn">
              <span className="action-icon">📞</span>
              <span className="contact-number">{contactInfo.phone}</span>
            </button>

            <Link to="/cart" onClick={() => handleNavClick('/cart')} className="action-btn cart-btn">
              <span className="action-icon">🛒</span>
              {itemCount > 0 && <span className="cart-count">{itemCount}</span>}
            </Link>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`menu-btn ${isMenuOpen ? 'open' : ''}`}
              aria-label="Menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`mobile-menu ${isMenuOpen ? 'active' : ''}`}>
          <div className="mobile-menu-content">
            <div className="mobile-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => handleNavClick(link.path)}
                  className={`mobile-link ${location.pathname === link.path ? 'active' : ''}`}
                >
                  <span className="mobile-link-icon">{link.icon}</span>
                  <span className="mobile-link-text">{link.label}</span>
                </Link>
              ))}
            </div>
            
            <div className="mobile-contact">
              <div className="mobile-contact-title">⚡ Quick Connect</div>
              <button onClick={handleCallNow} className="mobile-contact-item">
                <span className="contact-icon">📞</span>
                <span className="contact-info">{contactInfo.phone}</span>
                <span className="mobile-action">Call Now</span>
              </button>
              <button onClick={handleTextNow} className="mobile-contact-item">
                <span className="contact-icon">💬</span>
                <span className="contact-info">{contactInfo.text}</span>
                <span className="mobile-action">Text Us</span>
              </button>
              <div className="mobile-contact-item">
                <span className="contact-icon">📍</span>
                <div className="contact-info">
                  <div>{contactInfo.address}</div>
                  <div className="mobile-hours">{contactInfo.hours}</div>
                </div>
              </div>
              <div className="delivery-badge">
                🚚 Free Delivery • 30-45 Min • Discreet Packaging
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Connect With Pot Castle</h2>
              <button className="modal-close" onClick={() => setShowContactModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <button onClick={handleCallNow} className="modal-contact">
                <div className="modal-icon call">📞</div>
                <div className="modal-content">
                  <div className="modal-label">Call us now</div>
                  <div className="modal-value">{contactInfo.phone}</div>
                  <div className="modal-hours">Available 24/7</div>
                </div>
                <span className="modal-arrow">→</span>
              </button>
              <button onClick={handleTextNow} className="modal-contact">
                <div className="modal-icon text">💬</div>
                <div className="modal-content">
                  <div className="modal-label">Text us</div>
                  <div className="modal-value">{contactInfo.text}</div>
                  <div className="modal-hours">Quick response</div>
                </div>
                <span className="modal-arrow">→</span>
              </button>
              <div className="modal-contact address">
                <div className="modal-icon address">📍</div>
                <div className="modal-content">
                  <div className="modal-label"> Hours</div>
                  <div className="modal-value">{contactInfo.address}</div>
                  <div className="modal-hours">{contactInfo.hours}</div>
                </div>
              </div>
              <div className="modal-email">
                <div className="email-icon">✉️</div>
                <div className="email-content">
                  <div className="modal-label">Email us</div>
                  <div className="modal-value">{contactInfo.email}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        /* Modern CSS Variables */
        :root {
          --primary: #8b5cf6;
          --primary-dark: #7c3aed;
          --primary-light: #a78bfa;
          --secondary: #ec489a;
          --secondary-dark: #db2777;
          --accent: #10b981;
          --dark: #0f0f0f;
          --light: #ffffff;
          --gray-50: #fafafa;
          --gray-100: #f4f4f5;
          --gray-200: #e4e4e7;
          --gray-300: #d4d4d8;
          --gray-600: #52525b;
          --gray-700: #3f3f46;
          --gray-800: #27272a;
          --gray-900: #18181b;
          --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
          --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          --shadow-2xl: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          --transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @media (prefers-color-scheme: dark) {
          :root {
            --light: #0f0f0f;
            --dark: #fafafa;
            --gray-50: #18181b;
            --gray-100: #27272a;
            --gray-200: #3f3f46;
            --gray-300: #52525b;
          }
        }

        /* Header Styles */
        .header {
          position: sticky;
          top: 0;
          z-index: 1000;
          background: rgba(var(--light-rgb, 255, 255, 255), 0.95);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--gray-200);
          transition: var(--transition);
        }

        .header.scrolled {
          box-shadow: var(--shadow-md);
          background: rgba(var(--light-rgb, 255, 255, 255), 0.98);
        }

        .header-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 1rem 2rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 2rem;
        }

        /* Logo Styles */
        .logo {
          display: flex;
          align-items: center;
          gap: 1rem;
          text-decoration: none;
          transition: var(--transition);
        }

        .logo:hover {
          transform: scale(1.02);
        }

        .logo-icon {
          width: 3rem;
          height: 3rem;
          background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
          border-radius: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: var(--transition);
          box-shadow: var(--shadow-md);
        }

        .castle-icon {
          width: 1.75rem;
          height: 1.75rem;
          color: white;
        }

        .logo-text {
          display: flex;
          flex-direction: column;
        }

        .logo-name {
          font-size: 1.5rem;
          font-weight: 800;
          background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          line-height: 1.2;
          letter-spacing: -0.02em;
        }

        .logo-tagline {
          font-size: 0.75rem;
          color: var(--gray-600);
          font-weight: 500;
        }

        .logo-delivery {
          font-size: 0.7rem;
          color: var(--accent);
          font-weight: 600;
          display: none;
        }

        @media (min-width: 1024px) {
          .logo-delivery {
            display: block;
          }
        }

        /* Desktop Navigation */
        .nav {
          display: flex;
          gap: 0.5rem;
          background: var(--gray-100);
          padding: 0.5rem;
          border-radius: 2rem;
        }

        @media (max-width: 768px) {
          .nav {
            display: none;
          }
        }

        .nav-link {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.625rem 1.25rem;
          color: var(--gray-600);
          text-decoration: none;
          font-weight: 600;
          font-size: 0.875rem;
          border-radius: 2rem;
          transition: var(--transition);
        }

        .nav-icon {
          font-size: 1.125rem;
        }

        .nav-link:hover {
          color: var(--primary);
          background: var(--light);
          transform: translateY(-1px);
        }

        .nav-link.active {
          background: var(--light);
          color: var(--primary);
          box-shadow: var(--shadow-sm);
        }

        /* Actions */
        .actions {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .action-btn {
          position: relative;
          background: transparent;
          border: none;
          padding: 0.625rem 1rem;
          cursor: pointer;
          color: var(--gray-700);
          font-size: 1.125rem;
          border-radius: 2rem;
          transition: var(--transition);
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 500;
        }

        .action-btn:hover {
          background: var(--gray-100);
          transform: translateY(-2px);
        }

        .contact-btn {
          background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
          color: white;
          box-shadow: var(--shadow-sm);
        }

        .contact-btn:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }

        .contact-number {
          font-size: 0.875rem;
          font-weight: 600;
        }

        @media (max-width: 1024px) {
          .contact-number {
            display: none;
          }
          
          .contact-btn {
            padding: 0.625rem;
          }
        }

        .cart-btn {
          position: relative;
        }

        .cart-count {
          position: absolute;
          top: -4px;
          right: -4px;
          background: linear-gradient(135deg, var(--secondary), var(--primary));
          color: white;
          font-size: 0.7rem;
          font-weight: 700;
          min-width: 1.25rem;
          height: 1.25rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }

        /* Menu Button */
        .menu-btn {
          display: none;
          flex-direction: column;
          justify-content: space-around;
          width: 2rem;
          height: 2rem;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0;
        }

        @media (max-width: 768px) {
          .menu-btn {
            display: flex;
          }
        }

        .menu-btn span {
          width: 100%;
          height: 2px;
          background: var(--gray-700);
          border-radius: 2px;
          transition: var(--transition);
        }

        .menu-btn.open span:first-child {
          transform: rotate(45deg) translate(5px, 5px);
        }

        .menu-btn.open span:nth-child(2) {
          opacity: 0;
        }

        .menu-btn.open span:last-child {
          transform: rotate(-45deg) translate(5px, -5px);
        }

        /* Mobile Menu */
        .mobile-menu {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: var(--light);
          z-index: 999;
          transform: translateX(-100%);
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          overflow-y: auto;
        }

        .mobile-menu.active {
          transform: translateX(0);
        }

        .mobile-menu-content {
          padding: 5rem 1.5rem 2rem;
        }

        .mobile-nav {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin-bottom: 2rem;
        }

        .mobile-link {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          color: var(--gray-700);
          text-decoration: none;
          font-weight: 600;
          border-radius: 1rem;
          transition: var(--transition);
          background: var(--gray-50);
        }

        .mobile-link.active {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.1));
          color: var(--primary);
        }

        .mobile-link-icon {
          font-size: 1.25rem;
        }

        .mobile-link-text {
          font-size: 1rem;
        }

        .mobile-contact {
          background: var(--gray-50);
          border-radius: 1.5rem;
          padding: 1.5rem;
        }

        .mobile-contact-title {
          font-weight: 700;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--primary);
          margin-bottom: 1rem;
        }

        .mobile-contact-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          width: 100%;
          padding: 1rem;
          background: var(--light);
          border: 1px solid var(--gray-200);
          border-radius: 1rem;
          margin-bottom: 0.75rem;
          cursor: pointer;
          transition: var(--transition);
        }

        .mobile-contact-item:hover {
          border-color: var(--primary);
          transform: translateX(4px);
        }

        .contact-icon {
          font-size: 1.25rem;
        }

        .contact-info {
          flex: 1;
          text-align: left;
          font-weight: 500;
        }

        .mobile-action {
          color: var(--primary);
          font-weight: 600;
          font-size: 0.875rem;
        }

        .mobile-hours {
          font-size: 0.75rem;
          color: var(--gray-600);
          margin-top: 0.25rem;
        }

        .delivery-badge {
          margin-top: 1rem;
          padding: 0.75rem;
          background: linear-gradient(135deg, var(--accent), var(--primary));
          color: white;
          text-align: center;
          border-radius: 1rem;
          font-size: 0.75rem;
          font-weight: 600;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1100;
          padding: 1rem;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .modal {
          background: var(--light);
          border-radius: 2rem;
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          animation: slideUp 0.3s ease;
          box-shadow: var(--shadow-2xl);
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem;
          border-bottom: 1px solid var(--gray-200);
        }

        .modal-title {
          font-size: 1.5rem;
          font-weight: 700;
          background: linear-gradient(135deg, var(--primary), var(--secondary));
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }

        .modal-close {
          background: var(--gray-100);
          border: none;
          font-size: 1.25rem;
          cursor: pointer;
          color: var(--gray-600);
          width: 2.5rem;
          height: 2.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: var(--transition);
        }

        .modal-close:hover {
          background: var(--gray-200);
          transform: rotate(90deg);
        }

        .modal-body {
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .modal-contact {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.25rem;
          background: var(--gray-50);
          border: 1px solid var(--gray-200);
          border-radius: 1.25rem;
          cursor: pointer;
          transition: var(--transition);
          width: 100%;
          text-align: left;
        }

        .modal-contact:hover:not(.address) {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.1));
          border-color: var(--primary);
          transform: translateX(4px);
        }

        .modal-icon {
          width: 3rem;
          height: 3rem;
          border-radius: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: white;
          flex-shrink: 0;
        }

        .modal-icon.call {
          background: linear-gradient(135deg, var(--primary), var(--secondary));
        }

        .modal-icon.text {
          background: linear-gradient(135deg, var(--secondary), var(--primary));
        }

        .modal-icon.address {
          background: linear-gradient(135deg, var(--gray-600), var(--gray-700));
        }

        .modal-content {
          flex: 1;
        }

        .modal-label {
          font-size: 0.75rem;
          color: var(--gray-600);
          margin-bottom: 0.25rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .modal-value {
          font-weight: 700;
          font-size: 1rem;
        }

        .modal-hours {
          font-size: 0.75rem;
          color: var(--gray-600);
          margin-top: 0.25rem;
        }

        .modal-arrow {
          margin-left: auto;
          color: var(--primary);
          font-size: 1.25rem;
          transition: var(--transition);
        }

        .modal-contact:hover .modal-arrow {
          transform: translateX(4px);
        }

        .modal-email {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.25rem;
          background: var(--gray-50);
          border: 1px solid var(--gray-200);
          border-radius: 1.25rem;
        }

        .email-icon {
          width: 3rem;
          height: 3rem;
          background: var(--gray-200);
          border-radius: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
        }

        .email-content {
          flex: 1;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .header-container {
            padding: 0.75rem 1rem;
            gap: 1rem;
          }

          .logo-icon {
            width: 2.5rem;
            height: 2.5rem;
          }

          .castle-icon {
            width: 1.5rem;
            height: 1.5rem;
          }

          .logo-name {
            font-size: 1.25rem;
          }

          .logo-tagline {
            display: none;
          }
        }

        @media (max-width: 480px) {
          .header-container {
            padding: 0.625rem 1rem;
          }

          .logo-icon {
            width: 2rem;
            height: 2rem;
          }

          .castle-icon {
            width: 1.25rem;
            height: 1.25rem;
          }

          .logo-name {
            font-size: 1rem;
          }
        }

        /* Touch Device Optimizations */
        @media (hover: none) {
          .action-btn:active,
          .mobile-contact-item:active,
          .modal-contact:active {
            transform: scale(0.98);
          }
        }

        /* Reduced Motion */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </>
  );
};

export default Header;